import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  TextField, 
  Autocomplete, 
  CircularProgress, 
  Box, 
  Typography, 
  Chip,
  Paper
} from '@mui/material';
import { Person, Work, Badge } from '@mui/icons-material';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';

/**
 * EmployeeSearchField component for searching and selecting employees
 * Designed for streamlined use in ticket creation workflow
 */
const EmployeeSearchField = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required,
  disabled,
  placeholder
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Handle search when input changes
  useEffect(() => {
    if (inputValue.trim().length < 2) {
      setOptions([]);
      return;
    }

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout to debounce search
    const timeoutId = setTimeout(() => {
      const searchEmployees = async () => {
        setLoading(true);
        try {
          const token = getAuthToken();
          const response = await axios.get('/v1/employees/search-for-ticket', {
            params: { query: inputValue },
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data && response.data.data) {
            setOptions(response.data.data);
          }
        } catch (error) {
          console.error('Error searching employees:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      searchEmployees();
    }, 500); // 500ms debounce

    setSearchTimeout(timeoutId);

    // Cleanup function
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [inputValue]);

  // Render employee option
  const renderOption = (props, option) => (
    <li {...props}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Person sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" component="span">
            {option.firstName} {option.lastName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {option.employeeId && (
            <Chip 
              size="small" 
              icon={<Badge fontSize="small" />} 
              label={`ID: ${option.employeeId}`} 
              variant="outlined" 
            />
          )}
          {option.department && (
            <Chip 
              size="small" 
              icon={<Work fontSize="small" />} 
              label={option.department} 
              variant="outlined" 
            />
          )}
        </Box>
      </Box>
    </li>
  );

  // Get option label for display
  const getOptionLabel = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    
    return `${option.firstName} ${option.lastName}${option.employeeId ? ` (${option.employeeId})` : ''}`;
  };

  // Render selected employee
  const renderSelectedEmployee = () => {
    if (!value) return null;
    
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 1.5, 
          mt: 1, 
          display: 'flex', 
          flexDirection: 'column',
          borderColor: 'primary.main',
          borderWidth: 1
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Selected Employee:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Person sx={{ mr: 1, color: 'primary.main' }} />
          <Typography>
            {value.firstName} {value.lastName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
          {value.employeeId && (
            <Chip 
              size="small" 
              icon={<Badge fontSize="small" />} 
              label={`ID: ${value.employeeId}`} 
              variant="outlined" 
            />
          )}
          {value.department && (
            <Chip 
              size="small" 
              icon={<Work fontSize="small" />} 
              label={value.department} 
              variant="outlined" 
            />
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Autocomplete
        id={id}
        options={options}
        loading={loading}
        value={value}
        onChange={(event, newValue) => {
          onChange({
            target: {
              name,
              value: newValue
            }
          });
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={label}
            placeholder={placeholder || "Search by name, ID, or department"}
            error={error}
            helperText={helperText}
            required={required}
            disabled={disabled}
            onBlur={onBlur}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        noOptionsText="No employees found"
        filterOptions={(x) => x} // Disable built-in filtering
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      {renderSelectedEmployee()}
    </Box>
  );
};

EmployeeSearchField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};

export default EmployeeSearchField;
